import {API_URL} from "services/http/env";
import {ApiError, type Method, type unknownRecord} from "services/http/types";

import {handleSuccess} from "services/notification/handlers";

const createQueryString = (params: unknownRecord | undefined) => {
  if (!params) return "";

  const query = new URLSearchParams();

  const append = (key: string, value: unknown) => {
    if (value !== undefined && value !== null) query.append(key, String(value));
  };

  for (const key in params) {
    const value = params[key];

    if (Array.isArray(value)) {
      for (const v of value) append(key, v);
    } else {
      append(key, value);
    }
  }

  return query.toString();
};
export const createCompleteURL = (
  path: string,
  queryParams: unknownRecord | undefined,
) => {
  const queryString = createQueryString(queryParams);

  const url = [
    API_URL,
    API_URL.endsWith("/") ? "" : "/",
    path.trim().replace(/(^\/+)|(\/+$)/g, ""),
    queryString ? (path.includes("?") ? "&" : "?") : "",
    queryString,
  ];

  return url.join("");
};
export const replacePathParams = (
  pathTemplate: string,
  replacements: unknownRecord,
) => {
  const pathKeys = new Set<string>();

  // Replace path params like :id in /users/:id
  const path = pathTemplate.replace(/:(\w+)/g, (_, key) => {
    if (!(key in replacements) || replacements[key] === undefined)
      throw Error(`Missing param key "${key}" for "${pathTemplate}"`);

    pathKeys.add(key);

    return String(replacements[key]);
  });

  // Collect other args that are not path params
  const otherArgs: unknownRecord = {};
  for (const key of Object.keys(replacements))
    if (!pathKeys.has(key)) otherArgs[key] = replacements[key];

  // Return the final path and the other args
  return [path, otherArgs] as const;
};
export const pendingRequests = new Map<string, Promise<unknown>>();

export const BODY_METHODS = new Set<Method>(["POST", "PUT", "PATCH", "DELETE"]);

export const isFormData = (v: unknown): v is FormData =>
  typeof FormData !== "undefined" && v instanceof FormData;

export const handleMutation = async <
  T extends object | void = void,
  R = unknown,
  O extends object | undefined = undefined,
>(
  fn: (args: T, options?: O) => Promise<R>,
  args: T,
  successMsg?: string,
  errorMsg?: string,
  opts?: {
    requestOptions?: O;
    onSuccess?: (result: R) => void | Promise<void>;
    onError?: (error: ApiError) => void | Promise<void>;
    onFinally?: () => void | Promise<void>;
  },
) => {
  try {
    const result = await fn(args, opts?.requestOptions);

    if (successMsg) {
      handleSuccess(successMsg);
    }
    await opts?.onSuccess?.(result);

    return result;
  } catch (e) {
    await opts?.onError?.(e as unknown as ApiError);
    throw errorMsg ?? e;
  } finally {
    await opts?.onFinally?.();
  }
};
